import React, { useRef, useState } from 'react';
import { buildVesselBoardFlow } from '../../../utils/shipChanges';

/**
 * "Feeders converge on the current vessel, then split to destination ports."
 *
 * The bracket the Ship Tracking card has always drawn. Lifted out of
 * ShipmentCard so the map's details panel can render the same thing rather
 * than a second drawing of it — review asked for this back on the map, and
 * two copies of a diagram is two things to keep in step.
 *
 * Returns null when a shipment has neither feeders nor destinations, so a
 * caller can mount it unconditionally.
 */
function ySpread(count, height, pad = 14) {
  if (count <= 1) return [height / 2];
  const span = height - pad * 2;
  return Array.from({ length: count }, (_, i) => pad + (span * i) / (count - 1));
}

export default function VesselBoardFlow({ shipment, isDark }) {
  const flow = buildVesselBoardFlow(shipment);
  const boxRef = useRef(null);
  const [tip, setTip] = useState(null);
  const showTip = (e, text) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    const x = e.clientX - box.left + 14;
    const y = e.clientY - box.top - 32;
    setTip({
      text,
      x: Math.min(Math.max(4, x), Math.max(4, box.width - 140)),
      y: Math.max(4, y),
    });
  };
  if (!flow || (!flow.feeders.length && !flow.destinations.length)) return null;
  const feeders = flow.feeders;
  const dests = flow.destinations;
  const hasFeeders = feeders.length > 0;
  const rowH = 28;
  const h = Math.max(hasFeeders ? feeders.length : 1, dests.length, 1) * rowH + 12;
  const vbW = 360;
  const leftW = hasFeeders ? 108 : 0;
  const hubX = hasFeeders ? 130 : 8;
  const hubW = 102;
  const splitX = hubX + hubW;
  const rightX = hasFeeders ? 244 : 122;
  const leftYs = ySpread(Math.max(feeders.length, 1), h);
  const rightYs = ySpread(Math.max(dests.length, 1), h);
  const hubY = h / 2;
  const line = isDark ? '#6b7280' : '#9ca3af';
  const ink = isDark ? '#e5e7eb' : '#111827';
  const mute = isDark ? '#9ca3af' : '#6b7280';
  const hubFill = isDark ? '#282a29' : '#f3f4f6';
  const delayFill = isDark ? '#7f1d1d' : '#fef2f2';
  const delayInk = isDark ? '#fca5a5' : '#b91c1c';

  return (
    <div ref={boxRef} className="relative my-3 w-full min-w-0 overflow-visible">
      <svg
        viewBox={`0 0 ${vbW} ${h}`}
        width="100%"
        height="auto"
        preserveAspectRatio="xMidYMid meet"
        className="block max-w-full"
        role="img"
        aria-label={`${feeders.map((f) => f.name).join(', ')} into ${flow.hub}, then ${dests.map((d) => `${d.port} ${d.n}`).join(', ')}`}
      >
        {hasFeeders && feeders.map((f, i) => {
          const y = leftYs[i];
          return (
            <g key={`in-${f.name}`}>
              <foreignObject x={0} y={y - 14} width={leftW} height={28}>
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  onMouseEnter={(e) => f.n != null && showTip(e, `${f.n} container count`)}
                  onMouseMove={(e) => f.n != null && showTip(e, `${f.n} container count`)}
                  onMouseLeave={() => setTip(null)}
                  style={{ fontSize: 11, lineHeight: 1.15, color: ink, overflow: 'hidden' }}
                >
                  <div style={{ fontWeight: 500, wordBreak: 'break-word' }}>{f.name}</div>
                  {f.n != null && <div style={{ color: mute, fontSize: 10 }}>{f.n}</div>}
                </div>
              </foreignObject>
              <path
                d={`M ${leftW + 2} ${y} C ${hubX - 10} ${y}, ${hubX - 10} ${hubY}, ${hubX} ${hubY}`}
                fill="none"
                stroke={line}
                strokeWidth="1.25"
              />
            </g>
          );
        })}
        <rect x={hubX} y={hubY - 13} width={hubW} height={26} rx="13" fill={hubFill} />
        <foreignObject x={hubX + 4} y={hubY - 11} width={hubW - 8} height={22}>
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: ink,
              textAlign: 'center',
              lineHeight: '22px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {flow.hub}
          </div>
        </foreignObject>
        {dests.map((d, i) => {
          const y = rightYs[i];
          const delayed = Number(d.delay) >= 1;
          const label = `${d.port} ${d.n}${delayed ? ` +${Math.round(d.delay)}d` : ''}`;
          return (
            <g key={`out-${d.port}`}>
              <path
                d={`M ${splitX} ${hubY} C ${splitX + 14} ${hubY}, ${splitX + 14} ${y}, ${rightX} ${y}`}
                fill="none"
                stroke={delayed ? delayInk : line}
                strokeWidth="1.25"
              />
              {delayed && (
                <rect x={rightX} y={y - 11} width={vbW - rightX} height={22} rx="11" fill={delayFill} />
              )}
              <foreignObject x={rightX + 6} y={y - 11} width={vbW - rightX - 8} height={22}>
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  onMouseEnter={(e) => showTip(e, `${d.n} container count`)}
                  onMouseMove={(e) => showTip(e, `${d.n} container count`)}
                  onMouseLeave={() => setTip(null)}
                  style={{
                    fontSize: 11,
                    lineHeight: '22px',
                    color: delayed ? delayInk : ink,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </div>
              </foreignObject>
            </g>
          );
        })}
      </svg>
      {tip && (
        <div
          className={`pointer-events-none absolute z-20 rounded px-1.5 py-0.5 text-[0.625rem] whitespace-nowrap shadow ${
            isDark ? 'bg-[#111] text-gray-100' : 'bg-[#111827] text-white'
          }`}
          style={{ left: tip.x, top: tip.y }}
        >
          {tip.text}
        </div>
      )}
    </div>
  );
}
